import { create } from 'zustand';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

type ToastKind = 'success' | 'error' | 'info';
interface ToastItem {
  id: string;
  kind: ToastKind;
  message: string;
}

interface ToastStore {
  items: ToastItem[];
  push: (kind: ToastKind, message: string, ttlMs?: number) => void;
  dismiss: (id: string) => void;
}

export const useToast = create<ToastStore>((set, get) => ({
  items: [],
  push: (kind, message, ttlMs = 5000) => {
    const id = crypto.randomUUID();
    set((s) => ({ items: [...s.items, { id, kind, message }] }));
    setTimeout(() => get().dismiss(id), ttlMs);
  },
  dismiss: (id) => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
}));

const colors: Record<ToastKind, string> = {
  success: 'border-l-teal-500',
  error: 'border-l-red-500',
  info: 'border-l-ink-900',
};

export default function ToastViewport() {
  const { items, dismiss } = useToast();
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none w-[calc(100vw-2rem)] max-w-sm md:bottom-4 md:top-auto"
    >
      <AnimatePresence>
        {items.map((t) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, x: 32 }}
            className={cn(
              'pointer-events-auto bg-white border-l-4 shadow-lg rounded-md p-4 pr-2 flex items-start gap-3',
              colors[t.kind],
            )}
            role="alert"
          >
            <p className="flex-1 text-sm text-ink-900">{t.message}</p>
            <button
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
              className="text-neutral-500 hover:text-ink-900 transition-colors"
            >
              <X size={18} />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
